import { useEffect, useMemo, useState } from 'react'
import { isMobileOnly } from 'react-device-detect'

const BASE_WIDTH = 1920
const BASE_HEIGHT = 1080
const MIN_SCALE = 0.75
const MIN_SCALE_FLOOR = 0.55
const MAX_SCALE = 1
const VIEWPORT_TOLERANCE = 0.2
const VIEWPORT_BIAS = 1.12
const RESOLUTION_TOLERANCE = 0.1

const roundScale = (value) => Math.round(value * 100) / 100

const clampWithTolerance = (value: number, tolerance: number) => {
	const capped = Math.min(1, Math.max(0, value))
	return capped >= 1 - tolerance ? 1 : capped
}

const computeScale = () => {
	if (typeof window === 'undefined' || isMobileOnly) return 1

	const viewportWidth = window.visualViewport?.width ?? window.innerWidth
	const viewportHeight = window.visualViewport?.height ?? window.innerHeight

	const screenWidth = window.screen?.width ?? viewportWidth
	const screenHeight = window.screen?.height ?? viewportHeight
	const devicePixelRatio = window.devicePixelRatio ?? 1

	const baseDiagonal = Math.hypot(BASE_WIDTH, BASE_HEIGHT)
	const screenDiagonal = Math.hypot(screenWidth, screenHeight)
	const effectiveDiagonal = screenDiagonal * Math.sqrt(devicePixelRatio)

	const resolutionRatio = effectiveDiagonal / baseDiagonal
	const resolutionScale = clampWithTolerance(
		resolutionRatio,
		RESOLUTION_TOLERANCE
	)

	const widthRatio = viewportWidth / BASE_WIDTH
	const heightRatio = viewportHeight / BASE_HEIGHT
	const viewportRaw = Math.min(widthRatio, heightRatio)
	const viewportScale = clampWithTolerance(viewportRaw, VIEWPORT_TOLERANCE)

	const computed = Math.min(resolutionScale, viewportScale)
	const biasedViewport = viewportScale * VIEWPORT_BIAS
	const dynamicMin = Math.min(
		MIN_SCALE,
		Math.max(MIN_SCALE_FLOOR, biasedViewport)
	)
	const constrained = Math.min(MAX_SCALE, Math.max(dynamicMin, computed))

	return roundScale(constrained)
}

export default function useAppScale() {
	const [scale, setScale] = useState(() => computeScale())

	useEffect(() => {
		if (typeof window === 'undefined' || isMobileOnly) {
			if (scale !== 1) setScale(1)
			return undefined
		}

		const updateScale = () => {
			setScale((prev) => {
				const next = computeScale()
				return prev === next ? prev : next
			})
		}

		updateScale()

		window.addEventListener('resize', updateScale)
		window.addEventListener('orientationchange', updateScale)

		return () => {
			window.removeEventListener('resize', updateScale)
			window.removeEventListener('orientationchange', updateScale)
		}
	}, [])

	return useMemo(() => (isMobileOnly ? 1 : scale), [scale])
}
